import { useState, ComponentType, FC, HTMLAttributes } from 'react'
import styled from 'styled-components'
import { useFormContext, useWatch } from 'react-hook-form'
import pluralize from 'pluralize'

import ChevronDown from '../icons/ChevronDown'
import Panel from '../display/Panel'
import { AGE_ANNOTATIONS } from '../../search/filters/common/QuantitySelect'
import {
  Wrapper as UntypedWrapper,
  Option,
  Label,
  Footer,
  CloseButton,
  renderStepper,
} from '../form/GuestsInput'

const Wrapper = UntypedWrapper as ComponentType<
  HTMLAttributes<HTMLDivElement> & {
    open?: boolean
    rounded?: boolean
    noMargin?: boolean
    error?: boolean
  }
>

const Trigger = styled.button.attrs({ type: 'button' })`
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 18px;

  text-align: left;
  appearance: none;
  background: transparent;
  border: 0;
  outline: 0;
`

const Dropdown = styled.div`
  position: absolute;
  left: -1px;
  right: -1px;
  top: 100%;

  padding: 18px;

  border: 1px solid ${({ theme }) => theme?.['-ds-neutral-400']?.value};
  border-top: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  background-color: white;

  z-index: 9;
`

type Props = {
  guests?: number
  capacity?: number
  rounded?: boolean
  noMargin?: boolean
  error?: boolean
  petsEnabled?: boolean
  pets?: number
  maxPets?: number
}

const GuestsInput: FC<Props> = ({
  guests = 1,
  capacity = 99,
  petsEnabled = false,
  pets = 0,
  maxPets = 10,
  ...rest
}) => {
  const [open, setOpen] = useState(false)
  const { register, setValue, control } = useFormContext()
  // Watch the registered hidden inputs so the stepper UI re-renders when the
  // value changes (without `useWatch` the stepper buttons render once with
  // the initial value and never update).
  const adults = useWatch({ control, name: 'booking[adults]' })
  const children = useWatch({ control, name: 'booking[children]' })
  const petsValue = useWatch({ control, name: 'booking[pets]' })

  const triggerLabel =
    petsEnabled && pets > 0
      ? `${pluralize('Person', guests, true)}, ${pluralize('Pet', pets, true)}`
      : pluralize('Person', guests, true)

  return (
    <Wrapper className="js-guests-input field" open={open} {...rest}>
      &nbsp;
      <Trigger onClick={() => setOpen((prev) => !prev)}>{triggerLabel}</Trigger>
      <Panel id="guests-input" open={open} onClose={() => setOpen(false)}>
        <Dropdown>
          <Option aria-label="Adults">
            <Label>
              Adults
              <div>{AGE_ANNOTATIONS.ADULT}</div>
            </Label>
            <div>
              {renderStepper({
                input: {
                  value: adults,
                  onChange: (next: number) =>
                    setValue('booking[adults]', next, { shouldDirty: true }),
                },
                guests,
                capacity,
                minValue: 1,
                increaseLabel: 'Increase adults',
                decreaseLabel: 'Decrease adults',
              })}
            </div>
          </Option>
          <Option aria-label="Children">
            <Label>
              Children
              <div>{AGE_ANNOTATIONS.CHILD}</div>
            </Label>
            <div>
              {renderStepper({
                input: {
                  value: children,
                  onChange: (next: number) =>
                    setValue('booking[children]', next, { shouldDirty: true }),
                },
                guests,
                capacity,
                increaseLabel: 'Increase children',
                decreaseLabel: 'Decrease children',
              })}
            </div>
          </Option>
          {petsEnabled && (
            <Option aria-label="Pets">
              <Label>
                Pets
                {maxPets === 0 && <div>No pets allowed</div>}
              </Label>
              <div>
                {renderStepper({
                  input: {
                    value: petsValue,
                    onChange: (next: number) =>
                      setValue('booking[pets]', next, { shouldDirty: true }),
                  },
                  guests: pets,
                  capacity: maxPets,
                  increaseLabel: 'Increase pets',
                  decreaseLabel: 'Decrease pets',
                })}
              </div>
            </Option>
          )}
          <Footer>
            <CloseButton onClick={() => setOpen(false)}>Close</CloseButton>
          </Footer>
        </Dropdown>
      </Panel>
      {/* Hidden registered inputs so the values are part of native HTML form
       * POSTs. The stepper UI above writes through setValue. */}
      <input type="hidden" {...register('booking[adults]')} />
      <input type="hidden" {...register('booking[children]')} />
      {petsEnabled && <input type="hidden" {...register('booking[pets]')} />}
      <span>
        <ChevronDown />
      </span>
    </Wrapper>
  )
}

export default GuestsInput
